import { useEffect, useState } from 'react';
import { Pencil, Plus, Server, Terminal, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';
import { cn } from '@/lib/utils';

interface Sandbox {
  id: string;
  name: string;
  hostLabel?: string | null;
  status: 'online' | 'offline' | 'needs_update';
  heartbeatScript: string;
  heartbeatIntervalS: number;
  workingDirs: string[];
  versionSeen?: string | null;
  lastHeartbeatAt?: string | null;
}

interface CreateResponse {
  sandbox: Sandbox;
  install_command: string;
}

const STATUS_DOT: Record<Sandbox['status'], string> = {
  online: 'bg-emerald-500',
  offline: 'bg-muted-foreground',
  needs_update: 'bg-amber-500',
};

export function SandboxesView() {
  const [items, setItems] = useState<Sandbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Sandbox | 'new' | null>(null);
  const [installInfo, setInstallInfo] = useState<{ id: string; cmd: string } | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const data = await api.get<Sandbox[]>('/api/sandboxes');
      setItems(data);
      setError(null);
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    const t = setInterval(reload, 5000);
    return () => clearInterval(t);
  }, []);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="Sandboxes"
        description="One per host. Each daemon connects back over WS."
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>no sandboxes yet</ItemGroupEmpty>}

        {items.map((s) => (
          <Item key={s.id}>
            <ItemMedia>
              <Server />
            </ItemMedia>
            <ItemContent>
              <ItemHeader>
                <span
                  className={cn('inline-block size-2 rounded-full', STATUS_DOT[s.status])}
                  aria-hidden
                />
                <ItemTitle>{s.name}</ItemTitle>
                {s.versionSeen && (
                  <span className="text-xs text-muted-foreground">v{s.versionSeen}</span>
                )}
              </ItemHeader>
              <ItemDescription>
                heartbeat <code className="rounded bg-muted px-1 py-0.5 text-[11px]">{s.heartbeatScript}</code>
                {' '}every {s.heartbeatIntervalS}s
              </ItemDescription>
              <ItemFooter>
                <span className="font-mono">{s.id}</span>
                {s.lastHeartbeatAt && <span>· {s.lastHeartbeatAt.slice(0, 19)}</span>}
              </ItemFooter>
            </ItemContent>
            <ItemActions>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    const cfg = await api.get<{ sandbox_id: string; auth_token: string }>(
                      `/api/sandboxes/${s.id}/config`,
                    );
                    const cmd = `curl -sSL ${location.origin}/install/${cfg.sandbox_id}/${cfg.auth_token} | sh`;
                    setInstallInfo({ id: s.id, cmd });
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Terminal />
                Install
              </Button>
              <Button variant="outline" size="icon" onClick={() => setEditing(s)}>
                <Pencil />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!(await showConfirm(`Delete sandbox "${s.name}"?`))) return;
                  try {
                    await api.del(`/api/sandboxes/${s.id}`);
                    void reload();
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Trash2 />
              </Button>
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>

      <SandboxDialog
        open={editing !== null}
        initial={editing === 'new' ? null : (editing as Sandbox | null)}
        onOpenChange={(open) => !open && setEditing(null)}
        onCreated={(r) => {
          setEditing(null);
          setInstallInfo({ id: r.sandbox.id, cmd: r.install_command });
          void reload();
        }}
        onSaved={() => {
          setEditing(null);
          void reload();
        }}
      />

      <InstallDialog info={installInfo} onOpenChange={(open) => !open && setInstallInfo(null)} />
    </ItemGroup>
  );
}

function SandboxDialog({
  open,
  initial,
  onOpenChange,
  onCreated,
  onSaved,
}: {
  open: boolean;
  initial: Sandbox | null;
  onOpenChange: (open: boolean) => void;
  onCreated: (r: CreateResponse) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [heartbeat, setHeartbeat] = useState('echo ok');
  const [interval, setInterval] = useState(5);
  const [workingDirsText, setWorkingDirsText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setHeartbeat(initial?.heartbeatScript ?? 'echo ok');
    setInterval(initial?.heartbeatIntervalS ?? 5);
    setWorkingDirsText((initial?.workingDirs ?? []).join('\n'));
    setErr(null);
  }, [open, initial]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    const working_dirs = workingDirsText
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    try {
      if (initial) {
        await api.patch(`/api/sandboxes/${initial.id}`, {
          name,
          heartbeat_script: heartbeat,
          heartbeat_interval_s: interval,
          working_dirs,
        });
        onSaved();
      } else {
        const r = await api.post<CreateResponse>('/api/sandboxes', {
          name,
          heartbeat_script: heartbeat,
          heartbeat_interval_s: interval,
          working_dirs,
        });
        onCreated(r);
      }
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New sandbox'}</DialogTitle>
          {!initial && (
            <DialogDescription>Spin up a logical execution environment.</DialogDescription>
          )}
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sb-name">Name</Label>
            <Input id="sb-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="my-mac" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-hb">Heartbeat script</Label>
            <Textarea id="sb-hb" value={heartbeat} onChange={(e) => setHeartbeat(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-int">Interval (s)</Label>
            <Input
              id="sb-int"
              type="number"
              min={1}
              value={interval}
              onChange={(e) => setInterval(Number(e.target.value))}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sb-dirs">Working directories</Label>
            <Textarea
              id="sb-dirs"
              value={workingDirsText}
              onChange={(e) => setWorkingDirsText(e.target.value)}
              placeholder="/Users/me/Projects/foo&#10;/srv/app"
              className="min-h-[80px] font-mono text-xs"
            />
            <p className="text-[11px] text-muted-foreground">
              One path per line. Agents on this sandbox pick from this list when set; empty means
              free-text cwd.
            </p>
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name} onClick={save}>
            {initial ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InstallDialog({
  info,
  onOpenChange,
}: {
  info: { id: string; cmd: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={info !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Install daemon</DialogTitle>
          <DialogDescription>
            Run on the target host. After install you'll need to set up the OAuth token (see daemon
            output).
          </DialogDescription>
        </DialogHeader>
        {info && (
          <>
            <div className="text-xs text-muted-foreground">
              Sandbox id: <code className="rounded bg-muted px-1 py-0.5">{info.id}</code>
            </div>
            <pre className="overflow-x-auto rounded-md border bg-muted p-3 text-xs">{info.cmd}</pre>
          </>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              if (info) navigator.clipboard?.writeText(info.cmd);
            }}
          >
            Copy
          </Button>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
